import { useState, useRef, useMemo } from 'react';
import {
  Box, Typography, Chip,
} from '@mui/material';
import TooltipIconButton from '../../components/TooltipIconButton';
import { Add, Edit, ContentCopy, DeleteOutline } from '@mui/icons-material';
import { DataGridPremium, GridColDef, GridRenderCellParams, GridRowParams, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { conversionTypesService } from '../../services/conversion-types.service';
import { useModalState } from '../../hooks/useModalState';
import { useServerPagination } from '../../hooks/useServerPagination';
import { useGridHeight } from '../../hooks/useGridHeight';
import { useUrlState, codecs } from '../../hooks/useUrlState';
import { useDebounced } from '../../hooks/useDebounced';
import { buildEntityWhere, resolveSelectedIds, emptySelection } from '../../utils/entity-list';
import EntityGridToolbar from '../../components/EntityGridToolbar';
import ConfirmDialog from '../../components/ConfirmDialog';

export default function ConversionTypesPage() {
  const queryClient = useQueryClient();
  const { setModal } = useModalState('conversion-type');
  const [selection, setSelection] = useState<GridRowSelectionModel>(emptySelection);
  const [search, setSearch] = useUrlState('q', '', codecs.str);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const filterRef = useRef<HTMLDivElement>(null);
  const gridHeight = useGridHeight(filterRef);
  const debouncedSearch = useDebounced(search);
  const where = useMemo(
    () => buildEntityWhere('conversion_type', { search: debouncedSearch, statuses: [], tagIds: [] }, ['name']),
    [debouncedSearch],
  );
  const { gridProps, offset, limit } = useServerPagination(50, where);

  const { data, isFetching } = useQuery({
    queryKey: ['conversion-types', where, offset, limit],
    queryFn: () => conversionTypesService.query({ where, offset, limit }),
    placeholderData: (prev) => prev,
  });

  const rows = useMemo(() => data?.data || [], [data]);
  const selectedIds = useMemo(() => resolveSelectedIds(selection, rows), [selection, rows]);
  const runDelete = async () => {
    setDeleting(true);
    try {
      await Promise.all(selectedIds.map((id) => conversionTypesService.delete(id)));
      queryClient.invalidateQueries({ queryKey: ['conversion-types'] });
      setSelection(emptySelection);
      setConfirmOpen(false);
    } finally { setDeleting(false); }
  };

  const columns: GridColDef[] = [
    { field: 'name', headerName: 'Name', flex: 1 },
    {
      field: 'event_keys', headerName: 'Event Keys', width: 250,
      renderCell: (p: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center', height: '100%' }}>
          {(p.value || []).map((k: string) => (
            <Chip key={k} label={k} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
          ))}
        </Box>
      ),
    },
    {
      field: 'include_in_conversions', headerName: 'In Conversions', width: 130,
      renderCell: (p: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}><Chip label={p.value ? 'Yes' : 'No'} size="small" color={p.value ? 'success' : 'default'} /></Box>
      ),
    },
    {
      field: 'include_in_revenue', headerName: 'In Revenue', width: 120,
      renderCell: (p: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}><Chip label={p.value ? 'Yes' : 'No'} size="small" color={p.value ? 'success' : 'default'} /></Box>
      ),
    },
    {
      field: 'status', headerName: 'Status', width: 100,
      renderCell: (p: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}><Chip label={p.value} size="small" color={p.value === 'active' ? 'success' : p.value === 'archived' ? 'warning' : 'default'} /></Box>
      ),
    },
    {
      field: 'created_at', headerName: 'Created', width: 110,
      valueFormatter: (v) => new Date(v).toLocaleDateString(),
    },
    {
      field: 'actions', headerName: '', width: 70, sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 0, alignItems: 'center', height: '100%' }}>
          <TooltipIconButton size="small" onClick={() => setModal({ open: true, id: params.row.id, reason: 'Update' })} title="Edit">
            <Edit sx={{ fontSize: 16 }} />
          </TooltipIconButton>
          <TooltipIconButton size="small" onClick={() => setModal({ open: true, id: params.row.id, reason: 'Duplicate' })} title="Duplicate">
            <ContentCopy sx={{ fontSize: 16 }} />
          </TooltipIconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box ref={filterRef} sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={600}>Conversion Types</Typography>
      </Box>

      <DataGridPremium
        rows={rows}
        columns={columns}
        loading={isFetching}
        {...gridProps(data?.total)}
        showToolbar
        slots={{ toolbar: EntityGridToolbar }}
        slotProps={{ toolbar: {
          search, onSearch: setSearch,
          actions: [{ icon: Add, hint: 'Add conversion type', onClick: () => setModal({ open: true, reason: 'Create' }) }],
          selectedCount: selectedIds.length,
          pluralName: 'conversion types',
          selectionActions: [
            { icon: DeleteOutline, hint: 'Delete', onClick: () => setConfirmOpen(true), color: 'inherit' as const },
          ],
        } }}
        checkboxSelection
        disableRowSelectionOnClick
        rowSelectionModel={selection}
        onRowSelectionModelChange={setSelection}
        getRowHeight={() => 'auto'}
        onRowDoubleClick={(params: GridRowParams, event: React.MouseEvent) => {
          const target = event.target as HTMLElement;
          if (target.closest('[data-field="actions"]')) return;
          setModal({ open: true, id: params.row.id, reason: 'Update' });
        }}
        sx={{ bgcolor: 'background.paper', borderRadius: 2, height: gridHeight }}
      />

      <ConfirmDialog
        open={confirmOpen}
        title={`Delete ${selectedIds.length} conversion type${selectedIds.length === 1 ? '' : 's'}?`}
        body="This cannot be undone. Conversions keep the event key they were recorded with, so nothing in the history is lost."
        confirmLabel="Delete"
        busyLabel="Deleting..."
        destructive
        busy={deleting}
        onConfirm={runDelete}
        onClose={() => setConfirmOpen(false)}
      />
    </Box>
  );
}
